import { DateTime } from 'luxon';
import moment, { Moment } from 'moment';

export const WARSAW_TZ = 'Europe/Warsaw';

const DEFAULT_FORMAT = 'yyyy-LL-dd HH:mm';

/** Current moment in the Europe/Warsaw zone (luxon 1.25 has no DateTime.now()). */
export const nowInWarsaw = (): DateTime => DateTime.local().setZone(WARSAW_TZ);

/** Start of today in Europe/Warsaw. */
export const todayInWarsaw = (): DateTime => nowInWarsaw().startOf('day');

/** Start of yesterday in Europe/Warsaw — default date for dashboards. */
export const yesterdayInWarsaw = (): DateTime =>
  nowInWarsaw().minus({ days: 1 }).startOf('day');

/** Parse an ISO string into a DateTime pinned to the Europe/Warsaw zone. */
export const toWarsawDateTime = (iso?: string | null): DateTime | null => {
  if (!iso) {
    return null;
  }
  const dt = DateTime.fromISO(iso, { zone: WARSAW_TZ });
  return dt.isValid ? dt : null;
};

/** Format an ISO string for display in Europe/Warsaw time. */
export const formatWarsaw = (
  iso?: string | null,
  format: string = DEFAULT_FORMAT,
): string => {
  const dt = toWarsawDateTime(iso);
  return dt ? dt.toFormat(format) : '—';
};

/** ISO date (yyyy-mm-dd) used as the `Date` GraphQL argument. */
export const toApiDate = (dt: DateTime): string => dt.toISODate();

/**
 * Convert a moment value (antd DatePicker) into a luxon DateTime, keeping the
 * same calendar day. The picker selects a date, not an instant — converting
 * through epoch millis would shift the day when the browser zone ≠ Warsaw.
 */
export const momentToLuxon = (m?: Moment | null): DateTime | null => {
  if (!m) {
    return null;
  }
  return DateTime.fromObject({
    year: m.year(),
    month: m.month() + 1,
    day: m.date(),
    zone: WARSAW_TZ,
  });
};

/**
 * Convert a luxon DateTime into a moment value for antd DatePicker, keeping the
 * same calendar day (no instant/zone conversion — see `momentToLuxon`).
 */
export const luxonToMoment = (dt?: DateTime | null): Moment | null => {
  if (!dt) {
    return null;
  }
  return moment({ year: dt.year, month: dt.month - 1, day: dt.day });
};
